// ContactsDrawer.jsx

import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef,
} from "react";
import {
    Drawer,
    Fab,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    Box,
    Button,
    Tooltip,
    Stack,
    TextField,
    InputAdornment,
    Divider,
    CircularProgress,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import NearMeIcon from "@mui/icons-material/NearMe";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { supabase } from "../../supabaseClient";
import {
    LS_GOOGLE_ACCESS,
    LS_GOOGLE_REFRESH,
    marginFab,
    separationFab,
    zIndexBotones,
    zIndexModals,
} from "./helpers";
import {
    refreshGoogleAccessToken,
    upsertUsersStoreRefresh,
} from "./GoogleAuth";

const PAGE_SIZE = 100;

// pick primary display name
const pickDisplayName = (person) => {
    const names = person?.names || [];
    const n = names[0] || {};
    return (
        n.displayName ||
        n.displayNameLastFirst ||
        n.unstructuredName ||
        [n.givenName, n.familyName].filter(Boolean).join(" ") ||
        ""
    );
};

const pickPrimaryPhotoUrl = (person) => {
    const photos = person?.photos || [];
    if (!photos.length) return "";
    const primary = photos.find((p) => p?.metadata?.primary && p?.url)?.url;
    const any = photos.find((p) => p?.url)?.url;
    return primary || any || "";
};

const cantLink = (flag) => !flag;

const ContactRow = ({
    person,
    canLink,
    onLink,
    mode = "link",
    onLocate,
    onCreate,
}) => {
    const name = useMemo(() => {
        const names = person?.names || [];
        return names[0]?.displayName || names[0]?.givenName || "";
    }, [person]);

    const imgUrl = useMemo(() => {
        const base = pickPrimaryPhotoUrl(person);
        return base || "";
    }, [person]);

    return (
        <ListItem
            divider
            dense
            sx={{
                py: 0.25,
                px: 1,
                cursor: mode === "link" && canLink ? "pointer" : "default",
            }}
            onClick={() => {
                // comment: only allow tap-to-link when mode is "link"
                if (mode !== "link") return;
                if (cantLink(canLink)) return;
                onLink && onLink(person);
            }}
        >
            <ListItemAvatar sx={{ minWidth: 44 }}>
                <Avatar
                    alt={name}
                    src={imgUrl || undefined}
                    sx={{ width: 28, height: 28, fontSize: 13 }}
                    imgProps={{
                        referrerPolicy: "no-referrer",
                        onError: (e) => (e.currentTarget.src = ""),
                    }}
                >
                    {name ? name.charAt(0).toUpperCase() : "?"}
                </Avatar>
            </ListItemAvatar>

            <ListItemText
                primary={name || "Sin nombre"}
                primaryTypographyProps={{ variant: "body2", noWrap: true }}
            />

            {mode === "link" ? (
                <Stack direction="row" spacing={0.5}>
                    {/* comment: create-from-contact button */}
                    <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                            e.stopPropagation();
                            onCreate && onCreate(person);
                        }}
                    >
                        Crear
                    </Button>

                    {/* comment: keep the existing "Ligar" action */}
                    <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!cantLink(canLink) && onLink) onLink(person);
                        }}
                        disabled={!canLink}
                    >
                        Ligar
                    </Button>
                </Stack>
            ) : (
                <Tooltip title="Localizar en el mapa">
                    <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onLocate) onLocate(person);
                        }}
                        startIcon={<NearMeIcon fontSize="small" />}
                    >
                        Localizar
                    </Button>
                </Tooltip>
            )}
        </ListItem>
    );
};

const ContactsDrawer = ({
    personas = [],
    selectedPersonaId = "",
    onLinked,
    // comment: new optional callback to move focus/select a persona in the board
    onLocatePersona,
    fabNumber = 0,
}) => {
    const [open, setOpen] = useState(false);
    const [session, setSession] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [personaId, setPersonaId] = useState("");

    // NEW: keep a local fallback for provider access token across refreshes
    const [googleToken, setGoogleToken] = useState(() =>
        typeof window !== "undefined"
            ? localStorage.getItem(LS_GOOGLE_ACCESS)
            : null
    );
    const [needsReauth, setNeedsReauth] = useState(false); // show "Reconnect Google" button when needed

    // comment: new local UI state for collapsible groups
    const [openAssigned, setOpenAssigned] = useState(true);
    const [openUnassigned, setOpenUnassigned] = useState(true);

    useEffect(() => {
        let active = true;

        // comment: initial session read on mount
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (!active) return;
            setSession(session);

            // comment: store tokens if present (usually right after OAuth)
            if (session?.provider_token) {
                localStorage.setItem(LS_GOOGLE_ACCESS, session.provider_token);
            }
            if (session?.provider_refresh_token) {
                localStorage.setItem(
                    LS_GOOGLE_REFRESH,
                    session.provider_refresh_token
                );
                // comment: persist refresh in UsersStore when available
                if (session?.user?.id) {
                    upsertUsersStoreRefresh(
                        session.user.id,
                        session.provider_refresh_token
                    );
                }
            }
        });

        // comment: keep tokens in sync on auth state changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, s) => {
            setSession(s);

            // comment: access token can appear on SIGNED_IN or TOKEN_REFRESHED
            if (
                (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") &&
                s?.provider_token
            ) {
                localStorage.setItem(LS_GOOGLE_ACCESS, s.provider_token);
            }

            // comment: refresh token might appear/rotate on SIGNED_IN and sometimes on TOKEN_REFRESHED
            if (
                (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") &&
                s?.provider_refresh_token
            ) {
                localStorage.setItem(
                    LS_GOOGLE_REFRESH,
                    s.provider_refresh_token
                );
                if (s?.user?.id) {
                    upsertUsersStoreRefresh(
                        s.user.id,
                        s.provider_refresh_token
                    );
                }
            }

            if (event === "SIGNED_OUT") {
                localStorage.removeItem(LS_GOOGLE_ACCESS);
                localStorage.removeItem(LS_GOOGLE_REFRESH);
            }
        });

        return () => {
            active = false;
            subscription?.unsubscribe();
        };
    }, []);

    // helper to safely clear reauth flag
    const setNeedsNoReauthSafe = () => setNeedsReauth(false);

    // Use session token or cached token after refresh
    const token = needsReauth ? null : session?.provider_token || googleToken;

    const handleReconnect = async () => {
        // comment: trigger Google OAuth with consent to obtain fresh access/refresh tokens
        await supabase.auth.signInWithOAuth({
            provider: "google",
            options: {
                scopes: "https://www.googleapis.com/auth/contacts.readonly",
                queryParams: { prompt: "consent", access_type: "offline" },
            },
        });
    };

    const refreshInFlightRef = React.useRef(null);

    const refreshOnce = React.useCallback(() => {
        if (!refreshInFlightRef.current) {
            refreshInFlightRef.current = refreshGoogleAccessToken().finally(
                () => {
                    refreshInFlightRef.current = null;
                }
            );
        }
        return refreshInFlightRef.current;
    }, []);

    useEffect(() => {
        (async () => {
            if (!googleToken) {
                const newAccess = await refreshOnce();
                if (newAccess) setGoogleToken(newAccess);
            }
        })();
    }, []);

    const [totalCount, setTotalCount] = useState(null);
    const sentinelRef = useRef(null);
    const fetchContacts = useCallback(
        async (pageToken = null) => {
            if (!token) {
                setNeedsReauth(true);
                setInitialLoaded(true);
                return;
            }
            setLoading(true);
            setError("");
            try {
                const url = new URL(
                    "https://people.googleapis.com/v1/people/me/connections"
                );
                url.searchParams.set("personFields", "names,photos,metadata");
                url.searchParams.set("pageSize", String(PAGE_SIZE));
                url.searchParams.set("sortOrder", "FIRST_NAME_ASCENDING");
                if (pageToken) url.searchParams.set("pageToken", pageToken);

                const res = await fetch(url.toString(), {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!res.ok) {
                    // comment: try to parse error for better handling
                    let code = res.status;
                    let msg = `HTTP ${res.status}`;
                    try {
                        const j = await res.json();
                        msg = j?.error?.message || msg;
                        code = j?.error?.code || res.status;
                    } catch (_) {}

                    if (code === 401) {
                        const newAccess = await refreshOnce();
                        if (newAccess) {
                            localStorage.setItem(LS_GOOGLE_ACCESS, newAccess);
                            setGoogleToken(newAccess);
                            const retry = await fetch(url.toString(), {
                                headers: {
                                    Authorization: `Bearer ${newAccess}`,
                                },
                            });
                            if (!retry.ok)
                                throw new Error("HTTP " + retry.status);
                            const data = await retry.json();
                            const newPeople = data.connections || [];
                            setContacts((prev) =>
                                pageToken ? [...prev, ...newPeople] : newPeople
                            );
                            setNextPageToken(data.nextPageToken || null);
                            if (typeof data.totalItems === "number")
                                setTotalCount(data.totalItems);
                            setInitialLoaded(true);
                            setNeedsNoReauthSafe();
                            return; // comment: finished successful retry
                        }
                        // comment: if cannot refresh, fall back to reauth flow
                        setNeedsReauth(true);
                        setInitialLoaded(true);
                        try {
                            localStorage.removeItem(LS_GOOGLE_ACCESS);
                        } catch {}
                        setGoogleToken(null);
                    }
                    throw new Error(msg);
                }

                const data = await res.json();
                console.log("Fetched contacts data:", data);
                if (typeof data.totalItems === "number")
                    setTotalCount(data.totalItems);
                const newPeople = data.connections || [];
                setContacts((prev) =>
                    pageToken ? [...prev, ...newPeople] : newPeople
                );
                setNextPageToken(data.nextPageToken || null);
                setInitialLoaded(true);
                setNeedsNoReauthSafe();
            } catch (e) {
                setError(e?.message || "Error cargando contactos");
            } finally {
                setLoading(false);
            }
        },
        [token]
    );

    useEffect(() => {
        if (open && token && !initialLoaded && !loading && !needsReauth) {
            fetchContacts(null);
        }
    }, [open, token, initialLoaded, loading, needsReauth, fetchContacts]);

    const handleLoadMore = () => {
        if (nextPageToken && !loading) fetchContacts(nextPageToken);
    };

    const filtered = useMemo(() => {
        const norm = (s) =>
            (s || "")
                .toLocaleLowerCase()
                .normalize("NFD")
                .replace(/\p{Diacritic}/gu, "");
        const q = norm(query);
        return (contacts || []).filter((p) => {
            const name = pickDisplayName(p);
            return q ? norm(name).includes(q) : true;
        });
    }, [contacts, query]);

    // comment: index personas that already have googleResourceName to split groups
    const linkedByResource = useMemo(() => {
        const m = new Map();
        (personas || []).forEach((p) => {
            if (p?.googleResourceName) {
                m.set(String(p.googleResourceName), p); // value is Persona row
            }
        });
        return m;
    }, [personas]);

    const { assignedList, unassignedList } = useMemo(() => {
        const a = [];
        const u = [];
        (filtered || []).forEach((person) => {
            const key = String(person?.resourceName || "");
            if (key && linkedByResource.has(key)) a.push(person);
            else u.push(person);
        });
        return { assignedList: a, unassignedList: u };
    }, [filtered, linkedByResource]);

    const linkContact = async (person) => {
        if (!personaId) {
            setError("Selecciona un NodoPersona primero.");
            return;
        }
        if (!session) {
            setError("Necesitas estar autenticado con Google.");
            return;
        }

        const resourceName = person?.resourceName || null;
        const photoUrl = pickPrimaryPhotoUrl(person) || null;
        // capture person-level etag if present (or first source etag)
        const etag =
            person?.etag || person?.metadata?.sources?.[0]?.etag || null;

        try {
            setLoading(true);
            setError("");

            const { error } =
                await // update your Persona row with linked Google data (camelCase fields kept)
                supabase
                    .from("Persona")
                    .update({
                        googleResourceName: resourceName,
                        googlePicURL: photoUrl,
                        googleEtag: etag,
                    })
                    .eq("persona_id", personaId)
                    .eq("owner_id", session.user.id);

            if (error) throw error;

            if (onLinked) await onLinked(); // refresh personas in board
            setOpen(false);
        } catch (e) {
            setError(e?.message || "No se pudo ligar el contacto.");
        } finally {
            setLoading(false);
        }
    };

    const createNodeFromContact = useCallback(
        async (person) => {
            if (!session) {
                setError("Necesitas estar autenticado para crear nodos.");
                return;
            }

            // comment: compute fields from the Google Person object
            const displayName = pickDisplayName(person) || "Sin nombre";
            const googleResourceName = person?.resourceName || null;
            const googlePicURL = pickPrimaryPhotoUrl(person) || null;
            const googleEtag =
                person?.etag || person?.metadata?.sources?.[0]?.etag || null;

            try {
                setLoading(true);
                setError("");

                // comment: insert Persona with google linkage already filled (no extra linking step needed)
                const { data, error: err } = await supabase
                    .from("Persona")
                    .insert([
                        {
                            display_name: displayName,
                            owner_id: session.user.id,
                            googleResourceName,
                            googlePicURL,
                            googleEtag,
                        },
                    ])
                    .select("persona_id")
                    .single();

                if (err) throw err;

                // comment: refresh upstream lists/board
                if (onLinked) await onLinked();

                // comment: optional UX: notify and keep drawer open
                // (si prefieres cerrar, descomenta la siguiente línea)
                // setOpen(false);
            } catch (e) {
                setError(
                    e?.message || "No se pudo crear el nodo desde el contacto."
                );
            } finally {
                setLoading(false);
            }
        },
        [session, onLinked]
    );

    // comment: locate handler maps Google contact to Persona and selects it
    const locateContact = useCallback(
        (person) => {
            const rn = String(person?.resourceName || "");
            const persona = rn ? linkedByResource.get(rn) : null;
            if (persona && onLocatePersona) {
                onLocatePersona(String(persona.persona_id));
                // comment: optional: close the drawer to reveal the board focus
                setOpen(false);
            }
        },
        [linkedByResource, onLocatePersona]
    );

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;

        // comment: avoid creating observer if there is nothing left to load
        if (!nextPageToken) return;

        const obs = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && nextPageToken && !loading) {
                    handleLoadMore(); // comment: will call fetchContacts(nextPageToken)
                }
            },
            {
                root: null, // comment: viewport (Drawer scroll container still works)
                rootMargin: "200px", // comment: prefetch a bit before reaching the end
                threshold: 0.1,
            }
        );

        obs.observe(el);
        return () => obs.disconnect();
    }, [nextPageToken, loading]);

    return (
        <>
            <Tooltip title="Contactos de Google">
                <Fab
                    aria-label="open contacts"
                    onClick={() => setOpen(true)}
                    color="primary"
                    sx={{
                        position: "fixed",
                        top: marginFab + separationFab * fabNumber,
                        left: marginFab,
                        zIndex: zIndexBotones,
                    }}
                >
                    <PeopleIcon />
                </Fab>
            </Tooltip>
            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        width: 380,
                        maxWidth: "90vw",
                        display: "flex",
                        flexDirection: "column",
                        zIndex: zIndexModals,
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}
                >
                    <PeopleIcon />
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        Contactos
                    </Typography>
                    <Fab
                        size="small"
                        color="default"
                        onClick={() => setOpen(false)}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </Fab>
                </Box>

                {/* Filters / search */}
                <Box sx={{ px: 2, display: "grid", gap: 1.25 }}>
                    {error && (
                        <Typography variant="body2" color="error">
                            {String(error)}
                        </Typography>
                    )}
                    {(!token || needsReauth) && (
                        <Button
                            variant="contained"
                            onClick={handleReconnect}
                            sx={{ alignSelf: "start" }}
                        >
                            Reconectar Google
                        </Button>
                    )}

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar contactos…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Divider />

                {/* Content area */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                        {/* comment: ASSIGNED contacts (with locate action) */}
                        <Accordion
                            expanded={openAssigned}
                            onChange={() => setOpenAssigned((v) => !v)}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    // comment: make header sticky at the top of the scrollable area
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    bgcolor: "background.paper",
                                    // comment: subtle separation from list content
                                    borderBottom: (theme) =>
                                        `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="subtitle2">
                                    Asignados ({assignedList.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                <List dense disablePadding>
                                    {assignedList.map((p, i) => (
                                        <ContactRow
                                            key={(p.resourceName || "a") + i}
                                            person={p}
                                            canLink={false}
                                            onLink={() => {}}
                                            mode="locate"
                                            onLocate={locateContact}
                                        />
                                    ))}
                                    {assignedList.length === 0 && (
                                        <Box sx={{ px: 2, py: 1 }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                No hay contactos asignados que
                                                coincidan con la búsqueda.
                                            </Typography>
                                        </Box>
                                    )}
                                </List>
                            </AccordionDetails>
                        </Accordion>

                        {/* comment: UNASSIGNED contacts (with link action) */}
                        <Accordion
                            expanded={openUnassigned}
                            onChange={() => setOpenUnassigned((v) => !v)}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    // comment: make header sticky at the top of the scrollable area
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    bgcolor: "background.paper",
                                    // comment: subtle separation from list content
                                    borderBottom: (theme) =>
                                        `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="subtitle2">
                                    Sin asignar ({unassignedList.length}{" "}
                                    cargados) (
                                    {totalCount - assignedList.length} totales)
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                {/* Sticky selector inside Details. Make sure AccordionSummary is NOT sticky. */}
                                <Box
                                    sx={{
                                        position: "sticky",
                                        top: { xs: 56, sm: 64 }, // keep visible under AppBar/headers
                                        zIndex: (theme) => theme.zIndex.appBar, // stay above list items
                                        p: 1.5,
                                        bgcolor: "background.paper",
                                        borderBottom: (theme) =>
                                            `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    {/* keep the same state and handlers */}
                                    <TextField
                                        fullWidth
                                        select
                                        size="small"
                                        label="NodoPersona a ligar"
                                        value={personaId}
                                        onChange={(e) =>
                                            setPersonaId(e.target.value)
                                        }
                                    >
                                        {personas.map((p) => (
                                            <MenuItem
                                                key={p.persona_id}
                                                value={String(p.persona_id)}
                                            >
                                                {p.display_name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>

                                {/* Small spacer so first list row doesn't feel cramped under the sticky box */}
                                <Box sx={{ height: 8 }} />

                                {/* Unassigned contacts list */}
                                <List dense disablePadding>
                                    {!initialLoaded && loading && (
                                        <Box
                                            sx={{
                                                p: 2,
                                                display: "flex",
                                                gap: 1,
                                                alignItems: "center",
                                            }}
                                        >
                                            <CircularProgress size={16} />
                                            <Typography variant="body2">
                                                Cargando…
                                            </Typography>
                                        </Box>
                                    )}

                                    {unassignedList.map((p, i) => (
                                        <ContactRow
                                            key={(p.resourceName || "u") + i}
                                            person={p}
                                            canLink={Boolean(personaId)}
                                            onLink={linkContact}
                                            onCreate={createNodeFromContact}
                                            mode="link"
                                        />
                                    ))}

                                    {unassignedList.length === 0 &&
                                        initialLoaded &&
                                        !loading && (
                                            <Box sx={{ px: 2, py: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    No hay contactos sin asignar
                                                    que coincidan con la
                                                    búsqueda.
                                                </Typography>
                                            </Box>
                                        )}
                                </List>

                                {/* Pagination controls */}
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ p: 2 }}
                                >
                                    <Button
                                        variant="contained"
                                        onClick={handleLoadMore}
                                        disabled={!nextPageToken || loading}
                                    >
                                        Cargar más
                                    </Button>
                                </Stack>

                                {/* Sentinel for infinite scroll */}
                                <Box ref={sentinelRef} sx={{ height: 1 }} />
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default ContactsDrawer;
